import type { ApmBase } from '@elastic/apm-rum';

import routes from '../routes/routes.constants';

import { getLastClickedName } from './apmClickTracking';

// Build a set of known static segments from the routes constants.
// Any URL segment not in this set is treated as a dynamic ID → ":id".
const KNOWN_SEGMENTS = new Set(
  Object.values(routes)
    .flatMap((route) => route.split('/'))
    .filter(Boolean)
    .filter((seg) => !seg.startsWith(':')), // exclude route params like :id, :patientId
);
// "embedded" is used as a prefix in Routes.tsx but isn't in routes.constants
KNOWN_SEGMENTS.add('embedded');

/**
 * Extract the route path from the URL hash and replace dynamic segments
 * with `:id` to avoid high-cardinality transaction names in APM.
 *
 * Uses the known segments derived from routes.constants.ts — anything
 * not recognized is assumed to be a dynamic ID.
 *
 * Examples:
 *   "#/annotator/64a1b2c3d4e5f6a7b8c9d0e1" → "/annotator/:id"
 *   "#/claims/abc123" → "/claims/:id"
 *   "#/uploader?source=pms" → "/uploader"
 */
export const getHashRoute = (): string => {
  const hash = window.location.hash;
  if (!hash || hash === '#' || hash === '#/') return '/';

  // Strip leading '#'
  let path = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!path.startsWith('/')) path = '/' + path;

  // Strip query string
  const queryIndex = path.indexOf('?');
  if (queryIndex !== -1) path = path.slice(0, queryIndex);

  // Replace unknown segments with :id
  const segments = path.split('/').filter(Boolean);
  const parameterized = segments.map((seg) =>
    KNOWN_SEGMENTS.has(seg) ? seg : ':id',
  );

  return '/' + parameterized.join('/');
};

/**
 * Initialize all APM filters and hash-based route tracking.
 */
export const initializeApmFilters = (apm: ApmBase) => {
  // Filter to rename user-interaction transactions with meaningful click names
  apm.addFilter((payload) => {
    try {
      if (payload.transactions) {
        payload.transactions.forEach((transaction: any) => {
          if (transaction.type === 'user-interaction') {
            const clickName = getLastClickedName();
            if (clickName) {
              transaction.name = clickName;
            } else {
              transaction.name = 'Click';
            }
          }
        });
      }
    } catch (error) {
      console.error('[APM] Filter error:', error);
    }
    return payload;
  });

  // Fix the initial page-load transaction name for hash routing.
  // APM names it "/" because window.location.pathname is always "/" with HashRouter.
  // At init time, the hash is still "#/" — the router sets the real hash shortly after.
  // We use the filter to rename the page-load transaction right before it's sent,
  // which gives the router time to set the correct hash.
  let hasPageLoadBeenRenamed = false;
  apm.addFilter((payload) => {
    if (hasPageLoadBeenRenamed) return payload;
    if (payload.transactions) {
      payload.transactions.forEach((transaction: any) => {
        if (transaction.type === 'page-load') {
          transaction.name = getHashRoute();
          hasPageLoadBeenRenamed = true;
        }
      });
    }
    return payload;
  });

  // Manually create route-change transactions for hash-based navigation.
  // @elastic/apm-rum only patches history.pushState/replaceState, but
  // createHashHistory() sets window.location.hash directly, so APM
  // never sees route changes.
  let previousHash = window.location.hash;
  window.addEventListener('hashchange', () => {
    const newHash = window.location.hash;
    if (newHash === previousHash) return;
    previousHash = newHash;

    const routeName = getHashRoute();
    const transaction = apm.startTransaction(routeName, 'route-change', {
      managed: true,
    });
    if (transaction) {
      requestAnimationFrame(() => {
        transaction.end();
      });
    }
  });
};
